import { ReactElement } from 'react';
import {
  IComponentVideoPlayerFields,
  IPageDoctorFields,
  IPageLocationFields,
} from 'types/contentful';
import { DynamicComponentTypes, PreparedComponentObject } from 'components';
import { BrandedSiteProps } from 'lib/routing';
import RichText from 'components/UI/Richtext';
import VideoPlayerNoWrapper from './components/VideoPlayerNoWrapper';
import { VideoTypes } from './components/VideoPlayerNoWrapper';
import type { Document } from '@contentful/rich-text-types';

type DoctorBiographyProps<K extends DynamicComponentTypes = DynamicComponentTypes, CP = unknown> = {
  content: IPageDoctorFields;
  officeInfo: IPageLocationFields | null;
  doctorVideo?: PreparedComponentObject<K, CP>[] | null;
  siteData: BrandedSiteProps;
  preview: boolean;
};

export default function DoctorBiography({
  content,
  doctorVideo,
}: DoctorBiographyProps): ReactElement {
  const doctorVideoComponents = (doctorVideo as
    | PreparedComponentObject<DynamicComponentTypes, IComponentVideoPlayerFields>[]
    | undefined)?.map((comp, index: number): ReactElement | undefined => {
    return (
      <div className={`pb-4 text-center`} key={index}>
        <div className={`inline-block w-full border-none`}>
          <div
            className={`border-0 [&_iframe]:border-0 [&_iframe]:outline-none [&_iframe]:ring-0`}
            style={{ padding: '56.25% 0 0 0', position: 'relative' }}
          >
            <VideoPlayerNoWrapper video={comp.fields.video as VideoTypes} />
          </div>
        </div>
      </div>
    );
  });

  // Determine which sections have content
  const sections = [
    { key: 'education', title: 'Education', content: content.education, id: 'education' },
    {
      key: 'honorsAwards',
      title: 'Honors and Awards',
      content: content.honorsAwards,
      id: 'honors_and_awards',
    },
    {
      key: 'membershipsAffiliations',
      title: 'Memberships and Affiliations',
      content: content.membershipsAffiliations,
      id: 'memberships_and_affiliations',
    },
  ].filter(
    (section): section is { key: string; title: string; content: Document; id: string } =>
      section.content !== undefined
  );

  return (
    <section
      id="about_doctor"
      className="px-site-component py-10 md:py-20"
      style={{ scrollMarginTop: '13rem' }}
    >
      <div className="flex items-center mb-8">
        <h2 className="bordered-heading bh-location-heading-item text-new-34 lg:text-4xl lg:leading-new-43 font-light text-primary-actual">
          About {content.firstName} {content.lastName}
        </h2>
      </div>

      <div className="lg:grid lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-7">
          {content.about && (
            <div className="text-primary-actual text-lg">
              <RichText
                document={content.about}
                classNames="prose prose-lg max-w-none wysiwyg-richtext"
              />
            </div>
          )}

          {doctorVideoComponents && doctorVideoComponents.length > 0 && (
            <div className="mt-8">{doctorVideoComponents}</div>
          )}
        </div>

        <div className="lg:col-span-5 mt-8 lg:mt-0 space-y-8 text-primary-actual">
          {sections.map((section) => (
            <div key={section.key} id={section.id} style={{ scrollMarginTop: '7rem' }}>
              <h3 className="font-heading text-xl md:text-2xl">{section.title}</h3>
              <RichText document={section.content} classNames="mt-3 prose max-w-none text-lg" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
